import type { AppDataConfig, FieldErrors, LeadfunnelConfig } from '@/types';
import { createSlice } from '@reduxjs/toolkit';
import { mergeWith } from 'es-toolkit';
import appData from '@/assets/config/app-data';
import htmlData from '@/assets/config/html-data';
import UrlQuery from '@/utils/url-query';
import { checkForOfficeHours, choosePrioCalculatorVersion } from '@/redux/config/config.utils';
import { DEFAULT_SCREEN_SIZE_BREAKPOINTS } from '@/utils/screen-size-breakpoints';

const optimizelyTests = (() => {
  const tests = import.meta.glob<Partial<AppDataConfig>>('@/assets/optimizely-tests/*.tsx', {
    eager: true,
    import: 'default',
  });
  return Object.entries(tests).reduce<Record<string, Partial<AppDataConfig>>>((acc, [filePath, component]) => {
    const fileNameWithExtension = filePath.split('/').pop() || '';
    const fileName = fileNameWithExtension.replace(/\.[^/.]+$/, '');
    acc[fileName] = component;
    return acc;
  }, {});
})();

const noFieldErrors: FieldErrors = {};

// es-toolkit's `merge` deep-merges arrays by index instead of overwriting them, which silently
// corrupts configs whenever an optimizely test's array (e.g. `questions`) has a different length
// than the base config's. The optimizely-tests doc comments promise "arrays will be overwritten" -
// this customizer makes that actually true.
const overwriteArrays = (_targetValue: unknown, sourceValue: unknown) =>
  Array.isArray(sourceValue) ? sourceValue : undefined;

const initialState = {
  ...appData,
  ...htmlData,
} satisfies LeadfunnelConfig as LeadfunnelConfig;

const ConfigSlice = createSlice({
  name: 'config',
  initialState,
  reducers: (create) => ({
    updateConfigWithOptimizelyValues: create.reducer((state) => {
      const stateWithWindowConfig = window.config ? mergeWith(state, window.config, overwriteArrays) : state;
      const test = window.optimizelyTestScenario && optimizelyTests[window.optimizelyTestScenario];
      return test ? mergeWith(stateWithWindowConfig, test, overwriteArrays) : stateWithWindowConfig;
    }),
  }),
  selectors: {
    config: (config) => config,
    // HtmlData
    cmpId: (config) => config.cmpId,
    // AppData
    page: (config) => config.page,
    googleLogin: (config) => config.googleLogin,
    dateFormatToSend: (config) => config.dateFormatToSend,
    externalIdGenerateMethod: (config) => config.externalIdGenerateMethod,
    iconPosition: (config) => config.iconPosition,
    cssVersion: (config) => config.cssVersion,
    dontLookForSecret: (config) => config.dontLookForSecret,
    header: (config) => config.header,
    headerEnableStickyMobileHead: (config) => config.header.enableStickyMobileHead,
    banner: (config) => config.banner,
    numericKeyboard: (config) => !!config.numericKeyboard,
    autofocusInput: (config) => !!config.autofocusInput,
    screenSizeBreakpoints: (config) => ({ ...DEFAULT_SCREEN_SIZE_BREAKPOINTS, ...config.screenSizeBreakpoints }),
    sign: (config) => config.sign,
    signWithEnvelopIcon: (config) => config.sign.withEnvelopIcon,
    signWithEmojies: (config) => config.sign.withEmojies,
    signTitleSuccessColor: (config) => config.sign.titleSuccessColor,
    signCongratulation: (config) => config.sign.congratulation,
    signForm: (config) => config.sign.form,
    signFormErrors: (config) => config.sign.form.errors ?? noFieldErrors,
    signCertificates: (config) => config.sign.certificates,
    article: (config) => config.article,
    keywordWhiteList: (config) => config.keywordWhiteList,
    selectedKeywordTitle: (config) => config.selectedKeywordTitle,
    domainName: (config) => config.domainName,
    isInternationalizationOn: (config) => config.isInternationalizationOn,
    prefix: (config) => config.prefix,
    switchSlidesMode: (config) => config.switchSlidesMode,
    questions: (config) => config.questions,
    questionsDefaultValues: (config) => {
      return config.questions.reduce((result, question) => {
        const key = question.key;
        const choices = question.choices;
        const value = choices
          ? question.choices?.reduce((value, choice) => {
              if (choice.isDefault || choice.isPreselected) return choice.value;
              return value;
            }, '')
          : '';
        return { ...result, [key]: value };
      }, {});
    },
    resultPage: (config) => config.resultPage,
    additionalQuestions: (config) => config.additionalQuestions,
    certificateLogos: (config) => config.certificateLogos,
    showPriceModal: (config) => config.showPriceModal,
    showPriceModalInsuranceCondition: (config) => config.showPriceModalInsuranceCondition,
    appendPoaToCTA: (config) => config.appendPoaToCTA,
    via: (config) => config.via,
    statics: (config) => config.statics,
    newClientPercentToShow: (config) => config.newClientPercentToShow,
    useDynamicLandingPage: (config) => config.useDynamicLandingPage,
    dynamicLandingPageVersion: (config) => config.dynamicLandingPageVersion,
    openHours: (config) => config.openHours,
    callDelayMinutes: (config) => config.callDelayMinutes,
    useAlternateSuccessPage: (config) => config.useAlternateSuccessPage,
    alternateSuccessPageVersion: (config) => config.alternateSuccessPageVersion,
    useInsoPriceRandomizer: (config) => config.useInsoPriceRandomizer,
    insoPriceVariations: (config) => config.insoPriceVariations,
    insoPriceDefault: (config) => config.insoPriceDefault,
    useThanksPageWithCalculation: (config) => config.useThanksPageWithCalculation,
    calculationPageUrlName: (config) => config.calculationPageUrlName,
    fidMapping: (config) => config.fidMapping,
    enableUploadFiles: (config) => config.enableUploadFiles,
    redirectToOnSubmit: (config) => config.redirectToOnSubmit,
    vollmachtApiUrl: (config) => config.vollmachtApiUrl,
    useIustaOvmLink: (config) => config.useIustaOvmLink,
    footerDomainName: (config) => config.footerDomainName,
    footerHomepageUrl: (config) => config.footerHomepageUrl,
    onSuccessPhpResolve: (config) => config.onSuccessPhpResolve,
    selectedKeyword: (config) => {
      const kwd = UrlQuery.get('kwd').string();
      if (!kwd) return '';
      const kwdNormalized = kwd
        .replace('+', '')
        .trim()
        .replace(/^\w/, (c) => c.toUpperCase())
        .replace(/\s{2,}/g, ' ');
      if (
        config.selectedKeywordTitle &&
        config.keywordWhiteList.findIndex((keyword) => {
          const keywordLowerCased = keyword ? keyword.toLowerCase() : '';
          return keywordLowerCased === kwdNormalized.toLowerCase();
        }) > -1
      ) {
        document.title = config.selectedKeywordTitle.trim() + ' - ' + kwdNormalized;
        return kwdNormalized || '';
      } else {
        return '';
      }
    },
    calculatedCallDelay: (config) => {
      try {
        if (!config.openHours) return 1;
        const day = new Date().getDay();
        const currentHour = new Date().getHours();
        const hoursStr = config.openHours[day];
        const match = hoursStr?.match(new RegExp(/^([01]\d|2[0-4]):([0-5]\d)-([01]\d|2[0-4]):([0-5]\d)$/));
        if (!match) return 1;
        const openingHour = Number(match[1]);
        const closingHour = Number(match[3]);
        return openingHour <= currentHour && currentHour < closingHour ? config.callDelayMinutes : 1;
      } catch {
        return 1;
      }
    },
    percentDynamicLandingPageOnPrio: (config) => {
      return checkForOfficeHours(config.openHours)
        ? config.percentDynamicLandingPageOnPrioInsideOfficeHours
        : config.percentDynamicLandingPageOnPrioOutSideOfficeHours;
    },
    prioCalculatorVersion: (config) => {
      const prio = choosePrioCalculatorVersion(config.usePrioCalculatorV2, config.usePrioCalculatorV3);
      return {
        v2: prio === 2 ? 2 : 0,
        v3: prio === 3 ? 3 : 0,
        selected: prio,
      };
    },
  },
});

export default ConfigSlice;
